import { initFirebase } from '../firebase'
import { collection, getDocs, query, orderBy } from 'firebase/firestore'

const { db } = initFirebase()

const getPlanType = (vendorProductId) => {
  if (!vendorProductId) return 'Unknown'
  const id = vendorProductId.toLowerCase()
  if (id.includes('weekly')) return 'Weekly'
  if (id.includes('monthly')) return 'Monthly'
  if (id.includes('yearly') || id.includes('annual')) return 'Yearly'
  return 'Unknown'
}

const toMs = (value) => {
  if (!value) return null
  if (typeof value === 'object' && value.seconds) {
    return value.seconds * 1000
  }
  const ms = new Date(value).getTime()
  return isNaN(ms) ? null : ms
}

export class FirebaseSubscriptionLifecycleService {
  async getCancellationLifecycles(onProgress = () => {}) {
    onProgress({ phase: 'events', status: 'start' })
    const adaptyRef = collection(db, 'adapty')
    const adaptySnap = await getDocs(query(adaptyRef, orderBy('event_datetime', 'asc')))
    const events = adaptySnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(e => !!e.customer_user_id && !!e.event_datetime)

    // Track which users have which environments (for classifying feedback-only fallback)
    const usersWithProduction = new Set()
    const usersSeenInAdapty = new Set()
    for (const e of events) {
      usersSeenInAdapty.add(e.customer_user_id)
      const env = (e.event_properties?.environment || '').toLowerCase()
      if (env !== 'sandbox') usersWithProduction.add(e.customer_user_id)
    }
    const sandboxOnlyUsers = new Set(
      [...usersSeenInAdapty].filter(uid => !usersWithProduction.has(uid))
    )
    onProgress({ phase: 'events', status: 'done', count: events.length })

    onProgress({ phase: 'feedbacks', status: 'start' })
    const feedbackRef = collection(db, 'feedback')
    const feedbackSnap = await getDocs(feedbackRef)
    const allFeedbacks = feedbackSnap.docs.map(d => ({ id: d.id, ...d.data() }))
    onProgress({ phase: 'feedbacks', status: 'done', count: allFeedbacks.length })

    onProgress({ phase: 'processing', status: 'start' })
    const relevantFeedbacks = allFeedbacks.filter(f =>
      (f.subscriptionCancelFeedback === true || f.isConvertedBack === true) && !!f.userId
    )

    const feedbacksByUser = {}
    for (const f of relevantFeedbacks) {
      if (!feedbacksByUser[f.userId]) feedbacksByUser[f.userId] = []
      feedbacksByUser[f.userId].push(f)
    }

    const eventsByUser = {}
    for (const e of events) {
      const uid = e.customer_user_id
      if (!eventsByUser[uid]) eventsByUser[uid] = []
      eventsByUser[uid].push(e)
    }

    const lifecycles = []

    for (const [userId, userEvents] of Object.entries(eventsByUser)) {
      userEvents.sort((a, b) => toMs(a.event_datetime) - toMs(b.event_datetime))

      const subStarts = userEvents.filter(e => e.event_type === 'subscription_started')
      const trialStarts = userEvents.filter(e => e.event_type === 'trial_started')
      const userFeedbacksForMatch = feedbacksByUser[userId] || []

      // Pair each cancel with the most recent preceding start of the same kind.
      // cancelType: 'subscription' (paid renewal off) or 'trial' (trial renewal off).
      const buildFor = (starts, cancels, cancelType) => {
        const out = []
        for (const cancel of cancels) {
          const cancelMs = toMs(cancel.event_datetime)
          if (cancelMs === null) continue

          const prevStarts = starts.filter(s => toMs(s.event_datetime) <= cancelMs)
          const start = prevStarts[prevStarts.length - 1]
          if (!start) continue

          const startMs = toMs(start.event_datetime)
          const durationMs = Math.max(0, cancelMs - startMs)

          const nextStart = starts.find(s => toMs(s.event_datetime) > cancelMs) || null

          let matchedFeedback = null
          let minDiff = Infinity
          for (const f of userFeedbacksForMatch) {
            const fMs = toMs(f.timestamp)
            if (fMs === null) continue
            const diff = Math.abs(fMs - cancelMs)
            if (diff < minDiff) {
              minDiff = diff
              matchedFeedback = f
            }
          }

          const cancelEnv = cancel.event_properties?.environment || 'Unknown'

          out.push({
            userId,
            cancelType,
            startDateMs: startMs,
            cancelDateMs: cancelMs,
            durationMs,
            vendorProductId: start.event_properties?.vendor_product_id || null,
            planType: getPlanType(start.event_properties?.vendor_product_id),
            feedback: matchedFeedback,
            repurchased: false,
            repurchasePackage: null,
            repurchasePlanType: null,
            repurchaseDateMs: null,
            environment: cancelEnv,
            _nextStart: nextStart
          })
        }
        return out
      }

      const userLifecycles = [
        ...buildFor(subStarts, userEvents.filter(e => e.event_type === 'subscription_renewal_cancelled'), 'subscription'),
        ...buildFor(trialStarts, userEvents.filter(e => e.event_type === 'trial_renewal_cancelled'), 'trial')
      ]

      // Attribute each isConvertedBack=true feedback to its closest cancel
      const userFeedbacks = feedbacksByUser[userId] || []
      const convertedFeedbacks = userFeedbacks.filter(f => f.isConvertedBack === true)
      for (const f of convertedFeedbacks) {
        if (userLifecycles.length === 0) continue
        const fMs = toMs(f.timestamp)
        if (fMs === null) continue
        let closest = null
        let minDiff = Infinity
        for (const lc of userLifecycles) {
          const diff = Math.abs(lc.cancelDateMs - fMs)
          if (diff < minDiff) {
            minDiff = diff
            closest = lc
          }
        }
        if (closest) {
          closest.repurchased = true
          closest.feedback = f
          if (closest._nextStart) {
            closest.repurchasePackage = closest._nextStart.event_properties?.vendor_product_id || null
            closest.repurchasePlanType = getPlanType(closest._nextStart.event_properties?.vendor_product_id)
            closest.repurchaseDateMs = toMs(closest._nextStart.event_datetime)
          }
        }
      }

      for (const lc of userLifecycles) {
        delete lc._nextStart
        lifecycles.push(lc)
      }
    }

    // Fallback: build feedback-only lifecycles for users with cancel feedback but no adapty cancel event
    const usersWithLifecycles = new Set(lifecycles.map(l => l.userId))
    for (const [userId, fs] of Object.entries(feedbacksByUser)) {
      if (usersWithLifecycles.has(userId)) continue
      let env
      if (sandboxOnlyUsers.has(userId)) env = 'Sandbox'
      else if (usersSeenInAdapty.has(userId)) env = 'Production'
      else env = 'Unknown'
      const cancelFeedbacks = fs.filter(f => f.subscriptionCancelFeedback === true || f.isConvertedBack === true)
      for (const f of cancelFeedbacks) {
        const fMs = toMs(f.timestamp)
        if (fMs === null) continue
        lifecycles.push({
          userId,
          cancelType: 'unknown',
          startDateMs: null,
          cancelDateMs: fMs,
          durationMs: null,
          vendorProductId: null,
          planType: 'Unknown',
          feedback: f,
          repurchased: f.isConvertedBack === true,
          repurchasePackage: null,
          repurchasePlanType: null,
          repurchaseDateMs: null,
          environment: env,
          feedbackOnly: true
        })
      }
    }

    lifecycles.sort((a, b) => b.cancelDateMs - a.cancelDateMs)
    onProgress({ phase: 'processing', status: 'done', count: lifecycles.length })
    return lifecycles
  }
}
